import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { fetchShiftDetails } from 'thunks/jobs';
import { incrementShiftRefCount, decrementShiftRefCount } from 'reducers/jobs';
import { isFeatureOn } from 'lib/features';
import { featureFlags } from 'config/featureFlags';
import { permissions } from 'config/permissions';
import FullSizeModal from 'components/FullSizeModal';
import Loading from 'components/Loading';
import Feature from 'components/Feature';
import { TimesheetDetailsHeader } from './TimesheetDetailsHeader';
import { TimesheetSubmission } from './TimesheetSubmission';
import { CandidateRatingForm } from './CandidateRatingForm';
import TimesheetMessages from './TimesheetMessages';
import { hasRecordedSubmission } from './timesheet-helpers';
import { TimesheetRejectModal } from './TimesheetRejectModal';
import TimesheetArchiveModal from './TimesheetArchiveModal';
import * as timesheetsThunks from '../../thunks/timesheets';
import './style.scss';


const { string, func, bool, arrayOf, object, objectOf } = PropTypes;
class TimesheetDetailsContainer extends Component {

  static propTypes = {
    fetchShiftDetails: func.isRequired,
    fetchTimesheetRejectReasons: func.isRequired,
    shiftKey: string.isRequired,
    candidateKey: string.isRequired,
    approveTimesheet: func.isRequired,
    rejectTimesheet: func.isRequired,
    goToTimesheetsListView: func.isRequired,
    adminGroups: arrayOf(object).isRequired,
    isRejectingTimesheets: bool.isRequired,
    isApprovingTimesheets: bool.isRequired,
    admin: objectOf(object),
    global: objectOf(object),
    adminOrgKey: string.isRequired,
  }

  static defaultProps = {
    admin: null,
    global: null,
  }

  state = { showRejectModal: false, showArchiveModal: false, archiveChange: null, submissionNotice: '', submissionDetails: null }

  handleDetailsLoaded = (timesheetKey, details) => {
    if (timesheetKey === this.props.timesheetKey) {
      this.setState({ submissionDetails: { timesheetKey, details } });
    }
  }

  componentDidMount = async () => {
    this.props.incrementShiftRefCount(this.props.shiftKey);
    this.props.fetchShiftDetails(this.props.shiftKey, this.props.candidateKey);
    this.props.fetchCandidateRatingCategories(this.props.candidateKey);
  }

  componentWillUnmount() {
    this.props.decrementShiftRefCount(this.props.shiftKey);
  }

  getPermissionsMessage = (shiftAdminGroups, adminIsInCareGroup) => {
    if (!this.props.adminCanApproveTimesheet && !this.props.adminCanRejectTimesheet) return 'You do not have the required permissions to approve or reject this timesheet.';
    if (!this.props.adminCanApproveTimesheet) return 'You do not have the required permissions to approve this timesheet.';
    if (!this.props.adminCanRejectTimesheet) return 'You do not have the required permissions to reject this timesheet.';
    if (shiftAdminGroups && !adminIsInCareGroup) return 'You do not belong to an admin group that can approve this timesheet.';
    return null;
  }

  render() {
    const { timesheetKey } = this.props;
    const archiveChange = this.state.archiveChange?.timesheetKey === timesheetKey ? this.state.archiveChange : null;
    const submissionNotice = archiveChange ? `Timesheet ${archiveChange.isArchived ? 'archived' : 'unarchived'}.` : this.state.submissionNotice;
    const details = this.state.submissionDetails?.timesheetKey === this.props.timesheetKey ? this.state.submissionDetails.details : null;
    const sourceBooking = {
      ...this.props.booking,
      ...details?.submission,
      ...(typeof details?.isArchived === 'boolean' ? { isArchived: details.isArchived } : {}),
      ...(details && Object.prototype.hasOwnProperty.call(details, 'status') ? { timesheetStatus: details.status } : {}),
    };
    const originalStatus = sourceBooking.timesheetStatus === 'archived'
      ? details?.submissionStatus ?? sourceBooking.submissionStatus ?? archiveChange?.status ?? null
      : sourceBooking.timesheetStatus;
    const headerBooking = {
      ...sourceBooking,
      ...(archiveChange ? { isArchived: archiveChange.isArchived, timesheetStatus: archiveChange.isArchived ? 'archived' : originalStatus } : {}),
    };
    const isArchived = archiveChange ? archiveChange.isArchived : headerBooking.isArchived === true || headerBooking.timesheetStatus === 'archived' || !!this.props.archived;
    const showTimesheetDetails = this.props.shift && this.props.booking && !this.props.timesheetsError && !this.props.fetchingShiftDetails;
    const shiftAdminGroups = this.props?.shift?.adminGroups ?? null;
    const adminIsInCareGroup = (shiftAdminGroups || []).some(adminGroup => this.props.adminGroups.includes(adminGroup.key));
    const timesheetPermissionsMessage = this.getPermissionsMessage(shiftAdminGroups, adminIsInCareGroup);
    const submittedRatings = this.props.booking?.submittedRatings;
    const orgHasRatingCategories = this.props.candidateRatingCategories.length > 0;
    const canArchive = this.props.canArchiveTimesheets && headerBooking.timesheetIsLocked && !isArchived;
    const canUnarchive = this.props.canArchiveTimesheets && isArchived;

    return (
      <FullSizeModal
        isOpen
        vflex
        onRequestClose={this.props.goToTimesheetsListView}
      >
        {this.props.isFetchingShiftDetails && (
          <div style={{ display: 'flex', height: '100%', width: '100%', justifyContent: 'center', alignItems: 'center' }}>
            <Loading />
          </div>
        )}
        {showTimesheetDetails && (
          <div className="timesheet-details">
            <div className="timesheet-details__row">
              <div className="timesheet-details__column column-left">
                <TimesheetDetailsHeader
                  shift={this.props.shift}
                  booking={headerBooking}
                  ratingsFeatureIsOn={this.props.candidateRatingCategories.length > 0}
                  servicesFeatureFlag={this.props.servicesFeatureFlag}
                  onArchive={canArchive ? () => this.setState({ showArchiveModal: true }) : undefined}
                  onUnarchive={canUnarchive ? () => this.setState({ showArchiveModal: true }) : undefined}
                />
                {submissionNotice && <p className="timesheet-details__notice" role="status">{submissionNotice}</p>}
                <TimesheetSubmission
                  onDetailsLoaded={this.handleDetailsLoaded}
                  archived={this.props.archived}
                  archiveOverride={archiveChange?.isArchived}
                  restoredStatus={originalStatus}
                  key={this.props.timesheetKey}
                  submissionRecorded={this.props.submissionRecorded}
                  shift={this.props.shift}
                  booking={this.props.booking}
                  approveTimesheet={this.props.approveTimesheet}
                  timesheetApprovalDisclaimer={this.props.timesheetApprovalDisclaimer}
                  openRejectModal={() => this.setState({ showRejectModal: true })}
                  isApprovingTimesheets={this.props.isApprovingTimesheets}
                  isRejectingTimesheets={this.props.isRejectingTimesheets}
                  timesheetRejectReasons={this.props.timesheetRejectReasons}
                  timesheetPermissionsMessage={timesheetPermissionsMessage}
                  timesheetStatus={this.props.booking.status}
                  shiftAdminGroups={shiftAdminGroups}
                  adminIsInCareGroup={adminIsInCareGroup}
                  adminOrgKey={this.props.adminOrgKey}
                  canManageLockedTimesheets={this.props.canManageLockedTimesheets}
                  onAdminSubmitted={async () => {
                    this.setState({ submissionNotice: 'Timesheet submitted for approval.' });
                    await Promise.all([this.props.fetchShiftDetails(this.props.shiftKey, this.props.candidateKey), this.props.refreshTimesheets()]);
                  }}
                />
                {orgHasRatingCategories && (
                  <CandidateRatingForm
                    candidateRatingCategories={this.props.candidateRatingCategories}
                    submitCandidateRatings={({ ratings, comment }) => this.props.submitCandidateRatings({ ratings, comment, shiftKey: this.props.shiftKey, candidateKey: this.props.candidateKey })}
                    submittedRatings={submittedRatings}
                    submittedReview={this.props.booking.submittedReview}
                    ratingSubmittedBy={this.props.booking.ratingSubmittedBy}
                    ratingSubmittedAt={this.props.booking.ratingSubmittedAt}
                    submittingCandidateRatings={this.props.submittingCandidateRatings}
                    adminIsInSameOrgAsShift={this.props.adminOrgKey === this.props.shift?.orgKey}
                  />
                )}
              </div>
              <div className="timesheet-details__column column-right">
                <TimesheetMessages
                  onRequestClose={this.props.goToTimesheetsListView}
                  shiftKey={this.props.shiftKey}
                  candidateKey={this.props.candidateKey}
                  timesheetKey={this.props.timesheetKey}
                />
              </div>
            </div>
          </div>
        )}
        {this.state.showArchiveModal && (canArchive || canUnarchive) && (
          <TimesheetArchiveModal
            key={this.props.timesheetKey}
            timesheetKey={this.props.timesheetKey}
            unarchive={isArchived}
            onClose={() => this.setState({ showArchiveModal: false })}
            onSuccess={() => {
              if (timesheetKey === this.props.timesheetKey) {
                this.setState({ showArchiveModal: false, archiveChange: { timesheetKey, isArchived: !isArchived, status: originalStatus }, submissionNotice: '' });
              }
              this.props.refreshTimesheets();
            }}
          />
        )}
        {this.state.showRejectModal && this.props.timesheetRejectReasons && (!shiftAdminGroups || adminIsInCareGroup) && (
          <Feature permissionRequired={permissions.TIMESHEETS_CAN_REJECT}>
            <TimesheetRejectModal
              declaration={this.props.timesheetApprovalDisclaimer}
              disabled={this.props.isApprovingTimesheets || this.props.isRejectingTimesheets}
              rejectReasons={this.props.timesheetRejectReasons.map(reason => ({ value: reason.key, label: reason.name }))}
              modalOpen={this.state.showRejectModal}
              onClose={() => this.setState({ showRejectModal: false })}
              rejectTimesheet={rejectReason => this.props.rejectTimesheet(this.props.timesheetKey, this.props.shiftKey, this.props.candidateKey, { reasonKey: rejectReason.value, reasonText: rejectReason.label })}
            />
          </Feature>
        )}
      </FullSizeModal>
    );
  }
}


function mapStateToProps({ user, global, timesheets, jobs }, ownProps) {

  const shift = jobs.details[ownProps.shiftKey];

  return {
    submissionRecorded: hasRecordedSubmission(timesheets, ownProps.timesheetKey),
    adminGroups: user.adminGroups,
    isRejectingTimesheets: timesheets.isRejectingTimesheets,
    isApprovingTimesheets: timesheets.isApprovingTimesheets,
    adminCanApproveTimesheet: isFeatureOn(null, permissions.TIMESHEETS_CAN_APPROVE, user, global),
    adminCanRejectTimesheet: isFeatureOn(null, permissions.TIMESHEETS_CAN_REJECT, user, global),
    canManageLockedTimesheets: isFeatureOn(null, 'timesheetsCanManageLockedTimesheets', user, global),
    canArchiveTimesheets: isFeatureOn(featureFlags.TIMESHEETS_ARCHIVING, permissions.TIMESHEETS_CAN_MANAGE_LOCKED_TIMESHEETS, user, global),
    timesheetRejectReasons: timesheets.timesheetRejectReasons,
    shift: shift?.details,
    booking: shift?.bookings?.find(booking => booking.candidateKey === ownProps.candidateKey && (!ownProps.timesheetKey || booking.timesheetKey === ownProps.timesheetKey)),
    isFetchingShiftDetails: shift?.isFetchingShiftDetails,
    timesheetsError: timesheets.timesheetsError,
    candidateRatingCategories: timesheets.candidateRatingCategories,
    submittingCandidateRatings: timesheets.submittingCandidateRatings,
    adminOrgKey: global.currentOrgKey,
    timesheetApprovalDisclaimer: global.orgConfig?.timesheetApprovalDisclaimer,
    servicesFeatureFlag: isFeatureOn(featureFlags.SERVICES, null, user, global),
  };
}

function mapDispatchToProps(dispatch, ownProps) {
  return {
    refreshTimesheets: () => dispatch(timesheetsThunks.fetchTimesheets()),
    approveTimesheet: (timesheetKey, shiftKey, candidateKey) => dispatch(timesheetsThunks.approveTimesheet(timesheetKey, shiftKey, candidateKey)),
    rejectTimesheet: (timesheetKey, shiftKey, candidateKey, rejectReason) => dispatch(timesheetsThunks.rejectTimesheet(timesheetKey, shiftKey, candidateKey, rejectReason)),
    fetchShiftDetails: (shiftKey, candidateKey) => dispatch(fetchShiftDetails(shiftKey, candidateKey)),
    fetchCandidateRatingCategories: candidateKey => dispatch(timesheetsThunks.fetchCandidateRatingCategories(candidateKey)),
    submitCandidateRatings: ({ ratings, comment, shiftKey, candidateKey }) => dispatch(timesheetsThunks.submitCandidateRatings({ ratings, comment, shiftKey, candidateKey })),
    incrementShiftRefCount: shiftKey => dispatch(incrementShiftRefCount(shiftKey)),
    decrementShiftRefCount: shiftKey => dispatch(decrementShiftRefCount(shiftKey)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(TimesheetDetailsContainer);
